import { useState } from 'react';
import { MagnifyingGlassIcon, UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDebounce } from '@odo/shared-ui';
import { useUsersList } from '../../../../hooks/useUsers';
import { useAddOrthodontistUser } from '../../../../hooks/useOrthodontistUsers';
import { useToast } from '../../../../providers/ToastProvider';
import type { UserListItem } from '../../../../services/users';
import { apiErrorMessage } from '../../../../utils/apiError';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { RoleBadge } from '../../users/RoleBadge';
import { UserCell } from '../../users/UserCell';

const SEARCH_DEBOUNCE_MS = 250;
const CANDIDATE_LIMIT = 8;

interface AddOrthodontistUserModalProps {
    orthodontistId: string;
    orthodontistName: string;
    /** User ids already members of this org - filtered out of the candidate list. */
    existingUserIds: Set<string>;
    onClose: () => void;
}

function CandidateRow({
    user,
    onPick
}: {
    user: UserListItem;
    onPick: (user: UserListItem) => void;
}) {
    return (
        <button
            type="button"
            onClick={() => onPick(user)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
        >
            <div className="flex-1 min-w-0">
                <UserCell name={user.fullname} email={user.email} size={32} />
            </div>
            <RoleBadge isAdmin={user.isAdmin} />
        </button>
    );
}

// Existing-user picker that posts straight to POST /orthodontist/:id/users -
// org membership is a real join table, so there is no "new user" pane here.
export function AddOrthodontistUserModal({
    orthodontistId,
    orthodontistName,
    existingUserIds,
    onClose
}: AddOrthodontistUserModalProps) {
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
    const [selected, setSelected] = useState<UserListItem | null>(null);

    const { users, isLoading } = useUsersList({
        page: 1,
        limit: CANDIDATE_LIMIT,
        search: debouncedSearch.trim() || undefined
    });
    const candidates = users.filter((user) => !existingUserIds.has(user.id));

    const addUser = useAddOrthodontistUser();

    useEscapeKey(onClose, !addUser.isPending);

    const handleConfirm = async () => {
        if (!selected) return;
        try {
            await addUser.mutateAsync({ orthodontistId, userId: selected.id });
            showToast(`${selected.fullname} added to ${orthodontistName}`, 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to add user to org'), 'error');
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-orthodontist-user-modal-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-8"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[440px] flex flex-col overflow-hidden">
                <div className="px-6 pt-5 pb-4 flex items-start gap-3.5">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 text-gray-500">
                        <UserPlusIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2
                            id="add-orthodontist-user-modal-title"
                            className="text-[17px] font-bold text-gray-900"
                        >
                            Add a user
                        </h2>
                        <p className="text-[13px] text-gray-600 leading-relaxed mt-1">
                            Link an existing account to{' '}
                            <span className="font-semibold text-gray-900">{orthodontistName}</span>.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={addUser.isPending}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 pb-5 flex flex-col gap-3">
                    {selected ? (
                        <div className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2.5">
                            <div className="flex-1 min-w-0">
                                <UserCell
                                    name={selected.fullname}
                                    email={selected.email}
                                    size={32}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelected(null)}
                                className="text-[12px] font-semibold text-gray-500 hover:text-gray-900 shrink-0"
                            >
                                Change
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 h-10">
                                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                                <input
                                    type="text"
                                    autoFocus
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search users by name or email"
                                    className="flex-1 min-w-0 text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
                                />
                            </div>
                            <div className="max-h-[260px] overflow-y-auto flex flex-col gap-0.5">
                                {isLoading && (
                                    <p className="px-3 py-3 text-[13px] text-gray-400">
                                        Searching…
                                    </p>
                                )}
                                {!isLoading && candidates.length === 0 && (
                                    <p className="px-3 py-3 text-[13px] text-gray-400">
                                        No matching users found.
                                    </p>
                                )}
                                {!isLoading &&
                                    candidates.map((user) => (
                                        <CandidateRow
                                            key={user.id}
                                            user={user}
                                            onPick={setSelected}
                                        />
                                    ))}
                            </div>
                        </>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2.5">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={addUser.isPending}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!selected || addUser.isPending}
                        onClick={handleConfirm}
                        className="bg-black text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {addUser.isPending ? 'Adding…' : `Add to ${orthodontistName}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
